import React, { useEffect, useRef } from 'react';
import { Radiation, Award } from 'lucide-react';

// Nuke Ready Celebration - Shows when a country reaches 100% enrichment
export default function NukeReadyCelebration({ country, onClose }) {
  const musicRef = useRef(null);
  const mechanicalRef = useRef(null);
  const alertRef = useRef(null);

  useEffect(() => {
    // Play all 3 layered sounds simultaneously
    const music = new Audio('/assets/audio/nuke-ready-music.mp3');
    const mechanical = new Audio('/assets/audio/nuke-silo-mechanical.mp3');
    const alert = new Audio('/assets/audio/nuke-silo-alert.mp3');

    music.volume = 0.7;
    mechanical.volume = 0.8;
    alert.volume = 0.6;

    musicRef.current = music;
    mechanicalRef.current = mechanical;
    alertRef.current = alert;

    // Start all sounds together
    Promise.all([
      music.play().catch(e => console.log('Music play failed:', e)),
      mechanical.play().catch(e => console.log('Mechanical play failed:', e)),
      alert.play().catch(e => console.log('Alert play failed:', e))
    ]);

    // Auto-close after 6 seconds
    const timer = setTimeout(() => {
      onClose();
    }, 6000);

    return () => {
      clearTimeout(timer);
      [music, mechanical, alert].forEach(audio => {
        audio.pause();
        audio.currentTime = 0;
      });
    };
  }, [onClose]);

  const getLeaderSprite = (countryCode) => {
    const spriteMap = {
      'USA': 'usa',
      'RUS': 'russia',
      'CHN': 'china',
      'EU': 'europe',
      'NK': 'nkorea'
    };
    const spriteName = spriteMap[countryCode] || countryCode.toLowerCase();
    return `/assets/leaders/${spriteName}-happy.png`;
  };

  const getNukeSprite = (countryCode) => {
    const spriteMap = {
      'USA': 'usa',
      'RUS': 'russia',
      'CHN': 'china',
      'EU': 'eu',
      'NK': 'nkorea'
    };
    const spriteName = spriteMap[countryCode] || countryCode.toLowerCase();
    return `/assets/nukes/nuke-${spriteName}.png`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-auto">
      {/* Backdrop with red alert flash */}
      <div 
        className="absolute inset-0 bg-red-900/40 backdrop-blur-md animate-pulse"
        onClick={onClose}
      />
      
      {/* Celebration Modal */}
      <div className="relative p-8 max-w-4xl w-full mx-4 border-4 border-red-500/80 rounded-lg bg-slate-900/90 backdrop-blur-xl shadow-2xl">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Radiation className="w-12 h-12 text-red-500 animate-spin" style={{ animationDuration: '3s' }} />
            <h2 className="font-heading text-4xl tracking-wider text-red-500 uppercase">
              ☢️ NUKE READY! ☢️
            </h2>
            <Radiation className="w-12 h-12 text-red-500 animate-spin" style={{ animationDuration: '3s' }} />
          </div>
          <p className="text-amber-400 font-mono text-xl uppercase tracking-widest">
            {country?.name} Has Completed Uranium Enrichment!
          </p>
        </div>

        {/* Leader + Nuke Display */}
        <div className="flex items-center justify-center gap-12 mb-6">
          {/* Happy Leader (Jumping) */}
          <div className="flex flex-col items-center">
            <img 
              src={getLeaderSprite(country?.code)}
              alt={country?.name}
              className="w-48 h-48 object-contain leader-happy"
              style={{
                filter: 'drop-shadow(0 0 30px rgba(239, 68, 68, 0.8))'
              }}
              onError={(e) => {
                // Fallback to normal sprite if happy doesn't exist
                const spriteMap = {
                  'USA': 'usa',
                  'RUS': 'russia',
                  'CHN': 'china',
                  'EU': 'europe',
                  'NK': 'nkorea'
                };
                const spriteName = spriteMap[country?.code] || country?.code.toLowerCase();
                e.target.src = `/assets/leaders/${spriteName}.png`;
              }}
            />
            <div className="text-red-400 font-bold text-lg mt-2">{country?.name}</div>
          </div>

          {/* Nuke Sprite */}
          <div className="flex flex-col items-center relative">
            <div className="absolute inset-0 bg-red-500/20 rounded-full blur-2xl animate-pulse" />
            <img 
              src={getNukeSprite(country?.code)}
              alt="Nuclear Missile"
              className="w-32 h-48 object-contain relative z-10"
              style={{
                filter: 'drop-shadow(0 0 40px rgba(239, 68, 68, 1))',
                animation: 'float 2s ease-in-out infinite'
              }}
            />
            <div className="text-red-500 font-mono font-bold text-sm mt-2 uppercase tracking-wider">
              Nuclear Warhead
            </div>
          </div>
        </div>

        {/* Message */}
        <div className="bg-red-900/30 border-2 border-red-500/50 rounded-lg p-4 text-center">
          <Award className="w-8 h-8 mx-auto mb-2 text-amber-400" />
          <p className="text-amber-300 font-mono text-lg">
            🎯 <strong>{country?.name}</strong> can now launch NUCLEAR STRIKES!
          </p>
          <p className="text-red-400 font-mono text-sm mt-2">
            ⚠️ Devastating 50% population loss + lingering fallout ⚠️
          </p>
        </div>

        {/* Close button */}
        <button
          onClick={onClose}
          className="mt-6 w-full launch-button px-6 py-3 text-sm uppercase tracking-wider"
        >
          Continue
        </button>
      </div>

      <style jsx>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }
      `}</style>
    </div>
  );
}
